import env from '@config/env';
import logger from '@libs/log';
import { nanoid } from '@reduxjs/toolkit';
import type { OpenAIRole } from '@state/utils/openai';
import { useCallback, useEffect, useRef, useState } from 'react';

export interface ChatMessage {
  id: string;
  message: string;
  sender: OpenAIRole;
  timestamp: string;
}

export interface UseStreamingChatOptions {
  onMessageUpdate: (messages: ChatMessage[]) => void;
  onError?: (error: Error) => void;
  tags?: string[];
  systemPrompt?: string;
}

export interface UseStreamingChatReturn {
  isStreaming: boolean;
  isLoading: boolean;
  waitingForFirstChunk: boolean;
  sendMessage: (message: string, currentMessages: ChatMessage[]) => Promise<void>;
  reset: () => void;
}

export const useStreamingChat = ({
  onMessageUpdate,
  onError,
  tags = ['stream', 'cartedo'],
  systemPrompt,
}: UseStreamingChatOptions): UseStreamingChatReturn => {
  const [isStreaming, setIsStreaming] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [waitingForFirstChunk, setWaitingForFirstChunk] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const reset = useCallback(() => {
    setIsStreaming(false);
    setIsLoading(false);
    setWaitingForFirstChunk(false);

    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  const sendMessage = useCallback(
    async (message: string, currentMessages: ChatMessage[]) => {
      if (isStreaming || isLoading) {
        return;
      }

      try {
        setIsLoading(true);
        setIsStreaming(true);
        setWaitingForFirstChunk(true);

        // Create abort controller for this request
        abortControllerRef.current = new AbortController();

        // Add user message
        const userMessage: ChatMessage = {
          id: nanoid(),
          message: message.trim(),
          sender: 'user',
          timestamp: new Date().toISOString(),
        };

        const updatedMessages = [...currentMessages, userMessage];
        onMessageUpdate(updatedMessages);

        // Add temporary assistant message for streaming
        const tempAssistantMessage: ChatMessage = {
          id: nanoid(),
          message: '',
          sender: 'assistant',
          timestamp: new Date().toISOString(),
        };

        const messagesWithTemp = [...updatedMessages, tempAssistantMessage];
        onMessageUpdate(messagesWithTemp);

        // Convert messages to GPT format
        const gptMessages: { role: 'user' | 'assistant' | 'system'; content: string }[] =
          updatedMessages.map((msg) => ({
            role: msg.sender,
            content: msg.message,
          }));

        // Add system prompt if provided
        if (systemPrompt) {
          gptMessages.unshift({
            role: 'system',
            content: systemPrompt,
          });
        }

        // Make streaming request
        const response = await fetch(`${env.CARTEDO_GENAI_API_URL}/learner/copilot/stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': String(env.CARTEDO_GENAI_API_KEY),
          },
          body: JSON.stringify({
            messages: gptMessages,
            tags,
          }),
          signal: abortControllerRef.current.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${String(response.status)}`);
        }

        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error('No response body reader available');
        }

        const decoder = new TextDecoder();
        let buffer = '';
        let accumulatedMessage = '';

        // eslint-disable-next-line no-constant-condition, @typescript-eslint/no-unnecessary-condition
        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            setIsStreaming(false);
            setIsLoading(false);
            setWaitingForFirstChunk(false);
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() ?? ''; // Keep the last incomplete line in buffer

          for (const line of lines) {
            if (line.trim()) {
              try {
                // Parse AI SDK format: "0:" + content
                if (line.startsWith('0:')) {
                  const content = line.substring(2);

                  // Check for error in a structured way
                  try {
                    const parsed = JSON.parse(content) as { error?: string };
                    if (parsed.error) {
                      const error = new Error(`Streaming error: ${String(parsed.error)}`);
                      logger.error(error.message);
                      onError?.(error);
                      setIsStreaming(false);
                      setIsLoading(false);
                      return;
                    }
                  } catch {
                    // Not JSON error format, continue processing as content
                  }

                  // Content is sent directly as a string, but with quotes that need to be removed
                  // Remove only the outer quotes and properly handle escaped characters
                  let cleanContent = content;

                  // Remove leading and trailing quotes if they exist
                  if (cleanContent.startsWith('"') && cleanContent.endsWith('"')) {
                    cleanContent = cleanContent.slice(1, -1);
                  }

                  // Convert escaped characters back to their actual characters
                  cleanContent = cleanContent
                    .replace(/\\n/g, '\n') // Convert \n to actual newlines
                    .replace(/\\t/g, '\t') // Convert \t to actual tabs
                    .replace(/\\"/g, '"') // Convert \" to actual quotes
                    .replace(/\\\\/g, '\\'); // Convert \\ to actual backslashes

                  // Clear waiting state on first chunk
                  if (waitingForFirstChunk) {
                    setWaitingForFirstChunk(false);
                  }

                  accumulatedMessage += cleanContent;

                  // Update the temporary message with current content
                  const updatedMessagesWithContent = [
                    ...updatedMessages,
                    { ...tempAssistantMessage, message: accumulatedMessage },
                  ];
                  onMessageUpdate(updatedMessagesWithContent);
                }
              } catch (parseError) {
                // eslint-disable-next-line no-console
                console.warn('Failed to parse chunk:', line, parseError);
              }
            }
          }
        }
      } catch (error) {
        // Don't handle abort errors as errors
        if (error instanceof Error && error.name === 'AbortError') {
          return;
        }

        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        logger.error(`Error getting streaming completions: ${errorMessage}`);

        const errorObj = error instanceof Error ? error : new Error(errorMessage);
        onError?.(errorObj);

        setIsStreaming(false);
        setIsLoading(false);
        setWaitingForFirstChunk(false);
      } finally {
        abortControllerRef.current = null;
      }
    },
    [isStreaming, isLoading, waitingForFirstChunk, onMessageUpdate, onError, tags, systemPrompt]
  );

  return {
    isStreaming,
    isLoading,
    waitingForFirstChunk,
    sendMessage,
    reset,
  };
};
